import { useLazyQuery } from '@apollo/client/react';
import { useEffect } from 'react';

import { gql } from '@generated/gql';
import { useAuthContext } from '@services/Auth';
import { AssistantType } from '@src/types/attribution';
import { UserInformation } from '@src/types/auth';

const profileQuery = gql(`
  query ProfileInformation {
    profileInformation {
      ... on PartnerProfileInformation {
        name
        partnerPositionId
        partnerId
        handle
        isCorporate
        canDiscussInsurance
      }
      ... on SalesProfileInformation {
        email
      }
      ... on CustomerProfileInformation {
        accountNumber
      }
    }
  }
`);

export const useUserInformation = (): Nullable<UserInformation> => {
  const { getToken } = useAuthContext();

  const [getProfile, { data }] = useLazyQuery(profileQuery);

  useEffect(() => {
    (async () => {
      const token = await getToken();
      if (token) {
        getProfile();
      }
    })();
  }, [getToken, getProfile]);

  if (!data?.profileInformation) {
    return null;
  }

  const profile = data.profileInformation;

  switch (profile.__typename) {
    case 'PartnerProfileInformation':
      return {
        ...profile,
        type: AssistantType.PARTNER,
        isCorporate: profile.isCorporate,
        canDiscussInsurance: profile.canDiscussInsurance,
        profileName:
          profile.name || profile.handle || profile.partnerPositionId,
      };
    case 'SalesProfileInformation':
      return {
        ...profile,
        type: AssistantType.SALES,
        profileName: profile.email
          ? profile.email.split('@')[0]
          : 'Sales Agent',
      };
    default:
      return null;
  }
};
